import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Plus, Banknote } from "lucide-react";
import { toast, Toaster } from "sonner";

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [type, setType] = useState("savings");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // deposit/withdraw
  const [actAcct, setActAcct] = useState(null);
  const [actMode, setActMode] = useState("deposit");
  const [amount, setAmount] = useState("");

  const load = async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/accounts", { type, name: name || undefined });
      toast.success("Account opened");
      setOpenCreate(false); setName("");
      await load();
    } catch (e) { toast.error("Failed to open account"); }
    finally { setBusy(false); }
  };

  const doAction = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/transactions/${actMode}`, { account_id: actAcct.id, amount: parseFloat(amount) });
      toast.success(`${actMode === "deposit" ? "Deposited" : "Withdrew"} ${fmtUSD(parseFloat(amount))}`);
      setActAcct(null); setAmount("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Transaction failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <Toaster richColors position="top-right" />
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Wallet</div>
          <h1 className="font-display text-4xl mt-1">Accounts</h1>
        </div>
        <button
          onClick={() => setOpenCreate(true)}
          className="bg-[hsl(var(--primary))] text-white px-4 py-2.5 text-sm hover:bg-[hsl(var(--primary))]/90 flex items-center gap-2"
          data-testid="open-account-btn"
        >
          <Plus className="w-4 h-4" /> Open account
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((a) => (
          <div key={a.id} className="card-tech p-6 relative overflow-hidden" data-testid={`account-${a.id}`}>
            <div className="flex items-center justify-between">
              <span className="overline">{a.type}</span>
              <Banknote className="w-4 h-4 text-[hsl(var(--text-muted))]" />
            </div>
            <div className="font-display text-2xl mt-3">{a.name}</div>
            <div className="font-mono text-3xl mt-4">{fmtUSD(a.balance)}</div>
            <div className="font-mono text-xs text-[hsl(var(--text-muted))] mt-2" data-testid={`account-number-${a.id}`}>
              {a.account_number}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-6">
              <button
                onClick={() => { setActAcct(a); setActMode("deposit"); }}
                className="border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--surface-alt))]"
                data-testid={`deposit-btn-${a.id}`}
              >Deposit</button>
              <button
                onClick={() => { setActAcct(a); setActMode("withdraw"); }}
                className="border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--surface-alt))]"
                data-testid={`withdraw-btn-${a.id}`}
              >Withdraw</button>
            </div>
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="card-tech p-10 text-center text-[hsl(var(--text-muted))] col-span-full">
            No accounts yet. Open one to get started.
          </div>
        )}
      </div>

      {openCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpenCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 w-full max-w-md space-y-5">
            <h2 className="font-display text-2xl">Open new account</h2>
            <div>
              <label className="overline block mb-2">Account type</label>
              <div className="grid grid-cols-2 gap-2">
                {["checking", "savings"].map((t) => (
                  <button key={t} type="button" onClick={() => setType(t)}
                    className={`border px-4 py-3 text-sm capitalize ${type === t ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`}
                    data-testid={`type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Name (optional)</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))]"
                data-testid="account-name-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setOpenCreate(false)} className="border border-[hsl(var(--border))] px-4 py-2 text-sm" data-testid="cancel-account-btn">Cancel</button>
              <button type="submit" disabled={busy} className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm" data-testid="confirm-account-btn">
                {busy ? "Opening…" : "Open account"}
              </button>
            </div>
          </form>
        </div>
      )}

      {actAcct && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setActAcct(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={doAction} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 w-full max-w-md space-y-5">
            <h2 className="font-display text-2xl capitalize">{actMode} · {actAcct.name}</h2>
            <div className="overline">Available {fmtUSD(actAcct.balance)}</div>
            <div>
              <label className="overline block mb-2">Amount (USD)</label>
              <input type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono text-xl"
                data-testid="amount-input" autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setActAcct(null)} className="border border-[hsl(var(--border))] px-4 py-2 text-sm" data-testid="cancel-action-btn">Cancel</button>
              <button type="submit" disabled={busy} className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm capitalize" data-testid="confirm-action-btn">
                {busy ? "Processing…" : actMode}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Accounts;
